import { Alert, Button, Divider, Text, VStack, HStack, Link, Box } from '@televet/kibble-ui';
import { PimsIntegrationCapability } from 'shared/types/graphql';
import React, { forwardRef, useMemo, useState } from 'react';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { lastSuccessfulSync } from 'shared/providers/IntegrationsProvider/utils/timeFuncs';
import { format, isToday, isYesterday, parseISO } from 'date-fns';
import PimsSync, { ErrorMessageType } from 'shared/components/PimsSync';
import { ZENDESK_ARTICLE_BASE_URL } from 'shared/hooks/useZendesk';
import { useTracking } from 'shared/providers/TrackingProvider';
import { SettingsSyncStatusEvents } from 'shared/providers/TrackingProvider/EventNames';

export const SYNC_STATUS_HELP_ARTICLE_URL = `${ZENDESK_ARTICLE_BASE_URL}/13528150691981-VetData-Sync-Outages`;

const SyncStatusSettings = forwardRef((): JSX.Element => {
  const {
    syncStatus: { hasSyncOutage, lastSyncTime, isSyncPaused, primaryIntegration, timezoneName },
    primaryIntegrationName,
  } = useIntegrationsProvider();
  const { trackEvent } = useTracking();

  const [triggerSyncState, setTriggerSyncState] = useState(false);

  const formattedSyncTime = useMemo(() => {
    if (!lastSyncTime) return '';
    const parsedTime = parseISO(lastSyncTime);

    const formattedTime = format(parsedTime, 'h:mm a');

    if (isToday(parsedTime)) {
      return `today at ${formattedTime}`;
    } else if (isYesterday(parsedTime)) {
      return `yesterday at ${formattedTime}`;
    } else {
      return `on ${format(parsedTime, `MMM do 'at' h:mm a`)}`;
    }
  }, [lastSyncTime]);

  const lastSync = lastSuccessfulSync(
    primaryIntegration?.capabilities.map(({ capability, lastSyncedAt }) => ({ capability, lastSyncedAt })),
    timezoneName,
  );

  return (
    <VStack mt={5} spacing={5} align="flex-start" w="100%">
      <Text fontWeight="bold">{primaryIntegrationName} Sync Status</Text>
      {hasSyncOutage ? (
        <>
          <Alert
            status="warning"
            hideCloseButton
            title={`Your ${primaryIntegrationName} server is currently experiencing a sync outage`}
            description={`Your server was last synced ${formattedSyncTime}. You can keep working, but things like appointments, new clients, and conversation writebacks may not function correctly until the issue with your ${primaryIntegrationName} server has been resolved.`}
          />
        </>
      ) : isSyncPaused ? (
        <>
          <Alert
            status="warning"
            hideCloseButton
            title={`Syncs to your ${primaryIntegrationName} server are currently paused and will resume 1 hour before your clinic opens. Appointment Confirmations, Conversations, and Form Writebacks will still function. You can force a refresh by clicking the refresh button below.`}
          />
        </>
      ) : (
        <Alert hideCloseButton title={`Your ${primaryIntegrationName} server is currently in sync and up to date`} />
      )}
      {hasSyncOutage && (
        <Text>To learn more about sync outages, and for troubleshooting steps, click the button below:</Text>
      )}
      <HStack align="space-between" justify="space-between" w="100%">
        <Box>
          <Button
            onClick={(): void => {
              trackEvent(SettingsSyncStatusEvents.ViewKnowledgeBaseArticle, { source: 'Learn more button' });
              window.open(SYNC_STATUS_HELP_ARTICLE_URL);
            }}
            size="sm"
            variant="secondary"
          >
            {hasSyncOutage ? 'Learn More About Sync Outages' : 'Learn More'}
          </Button>
        </Box>
        <HStack align="flex-start" spacing={0}>
          {isSyncPaused && lastSync && timezoneName && (
            <Text
              style={{
                height: 'var(--chakra-sizes-8)',
                verticalAlign: 'middle',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              Last Synced at {lastSync.toLocaleDateString()} {lastSync.toLocaleTimeString()}
            </Text>
          )}
          {isSyncPaused && (
            <PimsSync
              triggerSearch={(): void => console.log('Running a sync')}
              shouldSync={triggerSyncState}
              resetSyncState={(): void => setTriggerSyncState(false)}
              parentComponent="Five-Minute Syncs"
              syncModels={[
                PimsIntegrationCapability.Appointment,
                PimsIntegrationCapability.ClinicPet,
                PimsIntegrationCapability.ClinicPetParent,
                PimsIntegrationCapability.ClinicPetToClinicPetParent,
                PimsIntegrationCapability.ClinicPetParentContactInfo,
              ]}
              errorType={ErrorMessageType.Toast}
              shouldShowLastSync={false}
              showSuccessMessage={true}
              message="Pets, Clients, and Appointments Refreshed"
            />
          )}
        </HStack>
      </HStack>
      <Divider />
      <Text size="sm" variant="subtle">
        Need help? Email us at{' '}
        <Link size="sm" href="mailto:support@otto.vet">
          support@otto.vet
        </Link>{' '}
        or click{' '}
        <Link
          size="sm"
          onClick={(): void => {
            if (window.zE) {
              window.zE('messenger', 'open');
            }
          }}
        >
          here
        </Link>{' '}
        to chat now.
      </Text>
    </VStack>
  );
});

SyncStatusSettings.displayName = 'SyncStatusSettings';

export default SyncStatusSettings;
